<template>
  <v-container flex>
    <v-data-table
      :headers="headers"
      :items="items"
      item-key="id"
      :single-expand="false"
      show-expand
      class="application-table"
    >
      <template v-slot:item.applicationDate="{ item }">
        <span class="nowrap">{{item.applicationDate | formatDate}}</span>
      </template>
      <template v-slot:item.amountCents="{ item }">
        <PricesDisplay
          :prices="item.amountCents"
          :contractCurrencies="currencies"
          :item-currency="getCurrencyForItem(item)"
          :chosen-currency="chosenCurrency"
        />
      </template>
      <template v-slot:item.nextAction="{ item }">
        <div :class="`${item.nextAction.nameClass} text-center`" >
          <small><span class="nowrap">{{item.nextAction.name}}</span></small>
        </div>
        <div :class="`${item.nextAction.dateClass} font-weight-medium text-center`">
          <small><span class="nowrap">{{item.nextAction.plannedDate | formatDate}}</span></small>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-row no-gutters class="no-wrap-row">
          <v-btn
            icon
            small
            @click="$emit('edit-payment', item)"
            :disabled="!canUserEdit"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <SubmitButton
            icon="mdi-delete"
            small
            :disabled="!!item.workflowActions.length || !canUserEdit"
            @click="$emit('delete-payment', item)"
          />
          <v-btn
            icon
            small
            @click="handleAddDocument(item)"
            :disabled="!canUserComment"
          >
            <v-icon>mdi-text-box-plus-outline</v-icon>
          </v-btn>
          <v-btn
            :disabled="contract.isMultiCurrency"
            @click="handleOpenCertificateWindow(item)"
            icon
            small
          >
            <v-icon>mdi-file-certificate-outline </v-icon>
          </v-btn>
        </v-row>
      </template>
      <template  v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-container>
            <v-row justify="center">
              <v-col cols="6" class="pl-0">
                <PaymentApplicationMilestonesTable
                  :payment-milestones="item.paymentMilestones"
                  :payment-applications="items"
                  :chosen-currency="chosenCurrency"
                  :currencies="currencies"
                />
              </v-col>
              <v-col cols="6" class="pl-0">
                <PaymentApplicationTimeline
                  :application="item"
                  @perform-action="(data) => performAction(data, item)"
                  @reset-actions="resetWorkflowActions(item)"
                  :can-user-edit="canUserEdit"
                />
              </v-col>
              <v-col cols="7" class="pl-0">
                <v-card>
                  <v-card-text>
                    <small><b>{{ $tc('document.document', 2) }}</b></small>
                    <documents-table dense :documents="item.documents"/>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </td>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import { DATE_FORMAT, getSumOfPrices } from '@/helpers'
import { addDocumentToPayment, getPaymentCertificate } from '@/services'
import { authContext } from '@/plugins/msalPlugin'
import PricesDisplay from '../common/PricesDisplay.vue'

export default {
  components: { PricesDisplay },
  name: 'PaymentsApplicationsTable',
  props: {
    value: {
      type: Array,
      default: () => ([])
    },
    paymentMilestones: Array,
    chosenCurrency: Object,
    currencies: Array,
    contract: Object
  },
  data () {
    return {
      headers: [
        { text: this.$t('common.name'), value: 'name' },
        { text: this.$t('common.description'), value: 'description' },
        { text: this.$t('payment.application.certificate'), value: 'paymentCertificate' },
        { text: this.$t('payment.application.invoiceRef'), value: 'invoiceReference' },
        { text: this.$t('payment.application.payment'), value: 'amountCents' },
        { text: this.$t('payment.application.date'), value: 'applicationDate' },
        { text: this.$t('payment.application.nextAction'), value: 'nextAction', sortable: false },
        { text: this.$t('payment.application.comment'), value: 'comment' },
        { text: '', value: 'actions', width: 100, sortable: false },
        { text: '', value: 'data-table-expand', sortable: false }
      ]
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit,
      canUserComment: state => state.userPermissionsToContract.canUserComment
    }),
    items () {
      return this.value.map(item => ({
        ...item,
        nextAction: this.nextActionForApplication(item),
        amountCents: getSumOfPrices(item.paymentMilestones, 'partAmountCents', this.currencies)
      }))
    }
  },
  methods: {
    async handleAddDocument (payment) {
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'DocumentForm',
        options: { maxWidth: 550 },
        scope: {
          title: this.$t('payment.application.addDocument', { name: payment.name })
        },
        handlers: {
          SAVE_DOCUMENT: async (document) => {
            await addDocumentToPayment(document, this.$route.params.contractId, payment.id)
            this.$eventBus.$emit('CLOSE_MODAL')
            this.$emit('applications-update')
          }
        }
      })
    },

    async handleOpenCertificateWindow (item) {
      const paymentCertificate = await getPaymentCertificate(this.$route.params.contractId)

      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'PaymentCertificate',
        options: { maxWidth: 1500 },
        scope: {
          title: paymentCertificate.title,
          payment: item,
          contract: this.contract,
          paymentMilestones: this.paymentMilestones,
          paymentCertificate,
          contractPrice: this.contractPrice,
          currencies: this.currencies,
          parts: this.parts,
          paymentApplications: this.items
        }
      })
    },
    async resetWorkflowActions (item) {
      const selectedItem = this.value.find(i => i.id === item.id)
      selectedItem.workflowActions = []
      this.$emit('update-payment', { ...selectedItem })
    },
    async performAction ({ action, applicationField, actualDate, comment }, item) {
      const selectedItem = this.value.find(i => i.id === item.id)
      selectedItem.workflowActions.push({
        name: action.name,
        actualDate,
        workflowStepId: action.id || action.workflowStepId,
        comment,
        user: authContext.account.name
      })
      this.$emit('update-payment', {
        ...selectedItem,
        ...applicationField
      })
    },
    getCurrencyForItem (item) {
      return item.paymentMilestones[0]?.currency
    },
    nextActionForApplication (item) {
      const nextDate = cloneDeep(item.workflowSteps.filter(i => i.isActive))
        .sort((a, b) => new Date(a.plannedDate) - new Date(b.plannedDate))[0]
      if (nextDate && nextDate.plannedDate) {
        const dateDiff = dayjs(nextDate.plannedDate, DATE_FORMAT).diff(dayjs(), 'day')
        nextDate.dateClass = dateDiff <= 0 ? 'error--text' : dateDiff <= 5 ? 'warning--text' : ''
      }
      return nextDate || { name: this.$t('payment.application.allSteps'), nameClass: 'success--text' }
    }
  }
}
</script>

<style>
.application-table > .v-data-table__wrapper > table > tbody > .v-data-table__expanded__content {
  box-shadow: none;
}
</style>
